import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme } from './theme-system/ThemeContext';
import Layout from './layout-system/Layout';
import HomeDeck from './website-pages/HomeDeck';

// Existing project pages
import TonelabDetailPage from './website-pages/TonelabDetailPage';
import MoodifyDetailPage from './website-pages/MoodifyDetailPage';
import FoundryDetailPage from './website-pages/FoundryDetailPage';
import ChibiDashDetailPage from './website-pages/ChibiDashDetailPage';
import NajdarDetailPage from './website-pages/NajdarDetailPage';
import NajdarStaysDetailPage from './website-pages/NajdarStaysDetailPage';
import TheSportshipDetailPage from './website-pages/TheSportshipDetailPage';

// New project pages
import AtlasDetailPage from './website-pages/AtlasDetailPage';
import BrainRotDetailPage from './website-pages/BrainRotDetailPage';
import CryptoScannerDetailPage from './website-pages/CryptoScannerDetailPage';
import DailyGratitudeDetailPage from './website-pages/DailyGratitudeDetailPage';
import EcoimpactDetailPage from './website-pages/EcoimpactDetailPage';
import FindbrandvoiceDetailPage from './website-pages/FindbrandvoiceDetailPage';
import ForlandingpageDetailPage from './website-pages/ForlandingpageDetailPage';
import OnepiecequizDetailPage from './website-pages/OnepiecequizDetailPage';
import PawtimoDetailPage from './website-pages/PawtimoDetailPage';
import PayrollSystemDetailPage from './website-pages/PayrollSystemDetailPage';
import PrivacyvaultDetailPage from './website-pages/PrivacyvaultDetailPage';
import ProveItDetailPage from './website-pages/ProveItDetailPage';
import RageLoggerDetailPage from './website-pages/RageLoggerDetailPage';
import SummitDetailPage from './website-pages/SummitDetailPage';
import TamAljanubDetailPage from './website-pages/TamAljanubDetailPage';
import TraydDetailPage from './website-pages/TraydDetailPage';
import VoiceReminderDetailPage from './website-pages/VoiceReminderDetailPage';
import ZodiacAppDetailPage from './website-pages/ZodiacAppDetailPage';
import LogoBuilderDetailPage from './website-pages/LogoBuilderDetailPage';
import MarketIntelHubDetailPage from './website-pages/MarketIntelHubDetailPage';
import PudeucDetailPage from './website-pages/PudeucDetailPage';
import RiyalbusinessDetailPage from './website-pages/RiyalbusinessDetailPage';
import StashDetailPage from './website-pages/StashDetailPage';

import './themes-and-styles/responsive.css';
import './themes-and-styles/themes.css';
import './App.css';

// /lab is a private design scratchpad — split it out so visitors never download it
const ConceptsLab = lazy(() => import('./home-concepts/ConceptsLab'));

// Scroll to top and track page views on route change
/* Private links that look like ordinary addresses. Each one counts the visit
   under its own source, then shows the plain home page at fahadnimer.com/ —
   no tags ever appear in the address bar. Read in admin/ (visitors dashboard). */
const TAGGED_LINKS = {
  '/portfolio': { source: 'application', medium: 'portfolio_link' }
};

function TaggedLanding({ tag }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!window.fnSkipTracking && window.gtag) {
      const loc = `${window.location.origin}/?utm_source=${tag.source}&utm_medium=${tag.medium}`;
      window.gtag('event', 'page_view', { page_path: '/', page_location: loc, page_title: document.title });
    }
    navigate('/', { replace: true, state: { counted: true } });
  }, [navigate, tag]);
  return null;
}

function ScrollToTop() {
  const { pathname, state } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);

    // Owner devices, local and tunnel visits are decided once in public/index.html
    // (window.fnSkipTracking); gtag is a no-op there.
    // a tagged link already sent this view (TaggedLanding) — don't count it twice
    if (!window.fnSkipTracking && window.gtag && !TAGGED_LINKS[pathname] && !state?.counted) {
      window.gtag('event', 'page_view', {
        page_path: pathname,
        page_title: document.title,
      });
    }
    // fahadnimer.com/in lands here as /?utm_source=linkedin…; once the visit is
    // counted, tidy the address bar so the visitor never sees the tracking tags
    if (/[?&]utm_/.test(window.location.search)) {
      setTimeout(() => window.history.replaceState(window.history.state, '', window.location.pathname + window.location.hash), 1500);
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Inner component that uses theme
function AppContent() {
  const { isDark } = useTheme();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Home-page concept lab — deliberately outside the site chrome */}
        <Route path="/lab" element={<Suspense fallback={null}><ConceptsLab /></Suspense>} />
        <Route path="*" element={<SiteShell />} />
      </Routes>
    </Router>
  );
};

const SiteShell = () => {
  return (
      <Layout>
        <Routes>
          <Route path="/" element={<HomeDeck />} />
          {Object.entries(TAGGED_LINKS).map(([path, tag]) => (
            <Route key={path} path={path} element={<TaggedLanding tag={tag} />} />
          ))}
          <Route path="/projects" element={<Navigate to="/" replace />} />

          {/* Existing projects */}
          <Route path="/projects/tonelab" element={<TonelabDetailPage />} />
          <Route path="/projects/moodify" element={<MoodifyDetailPage />} />
          <Route path="/projects/foundry" element={<FoundryDetailPage />} />
          <Route path="/projects/chibidash" element={<ChibiDashDetailPage />} />
          <Route path="/projects/najdar" element={<NajdarDetailPage />} />
          <Route path="/projects/najdar-stays" element={<NajdarStaysDetailPage />} />
          <Route path="/projects/the-sportship" element={<TheSportshipDetailPage />} />

          {/* New projects */}
          <Route path="/projects/atlas" element={<AtlasDetailPage />} />
          <Route path="/projects/brain-rot" element={<BrainRotDetailPage />} />
          <Route path="/projects/crypto-scanner" element={<CryptoScannerDetailPage />} />
          <Route path="/projects/daily-gratitude" element={<DailyGratitudeDetailPage />} />
          <Route path="/projects/ecoimpact-tracker" element={<EcoimpactDetailPage />} />
          <Route path="/projects/findbrandvoice" element={<FindbrandvoiceDetailPage />} />
          <Route path="/projects/forlandingpage" element={<ForlandingpageDetailPage />} />
          <Route path="/projects/onepiecequiz" element={<OnepiecequizDetailPage />} />
          <Route path="/projects/pawtimo" element={<PawtimoDetailPage />} />
          <Route path="/projects/payroll-system" element={<PayrollSystemDetailPage />} />
          <Route path="/projects/privacyvault" element={<PrivacyvaultDetailPage />} />
          <Route path="/projects/prove-it" element={<ProveItDetailPage />} />
          <Route path="/projects/rage-logger" element={<RageLoggerDetailPage />} />
          <Route path="/projects/summit" element={<SummitDetailPage />} />
          <Route path="/projects/tam-aljanub" element={<TamAljanubDetailPage />} />
          <Route path="/projects/trayd" element={<TraydDetailPage />} />
          <Route path="/projects/voice-reminder" element={<VoiceReminderDetailPage />} />
          <Route path="/projects/zodiac-app" element={<ZodiacAppDetailPage />} />
          <Route path="/projects/logo-builder" element={<LogoBuilderDetailPage />} />
          <Route path="/projects/market-intel-hub" element={<MarketIntelHubDetailPage />} />
          <Route path="/projects/pudeuc" element={<PudeucDetailPage />} />
          <Route path="/projects/riyalbusiness" element={<RiyalbusinessDetailPage />} />
          <Route path="/projects/stash" element={<StashDetailPage />} />
        </Routes>
      </Layout>
  );
};

// Main App with ThemeProvider
function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
